import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ReferenceLine, Legend,
} from "recharts";
import { Plus, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { fmtDate, Field } from "./shared";

export default function LipidsTab({ data, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    total_cholesterol_mg_dl: "",
    ldl_mg_dl: "",
    hdl_mg_dl: "",
    triglycerides_mg_dl: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const chartData = useMemo(() => (
    [...data.panels]
      .slice()
      .reverse()
      .map((p) => ({
        date: fmtDate(p.date),
        LDL: p.ldl_mg_dl,
        HDL: p.hdl_mg_dl,
        Trig: p.triglycerides_mg_dl,
        "Non-HDL": p.non_hdl_mg_dl,
      }))
  ), [data.panels]);

  async function submit(e) {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.post("/specialties/cardiology/lipids", {
        date: form.date,
        total_cholesterol_mg_dl: Number(form.total_cholesterol_mg_dl),
        ldl_mg_dl: Number(form.ldl_mg_dl),
        hdl_mg_dl: Number(form.hdl_mg_dl),
        triglycerides_mg_dl: Number(form.triglycerides_mg_dl),
      });
      toast.success("Lipid panel logged");
      setForm({
        date: new Date().toISOString().slice(0, 10),
        total_cholesterol_mg_dl: "", ldl_mg_dl: "", hdl_mg_dl: "", triglycerides_mg_dl: "",
      });
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save panel");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="cardio-lipids-tab">
      <form onSubmit={submit} className="ros-card space-y-4" data-testid="lipid-form">
        <div className="font-heading text-lg">Log a lipid panel</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Field label="Date">
            <input type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })}
                   data-testid="lipid-input-date"
                   className="ros-input" required />
          </Field>
          <Field label="Total cholesterol (mg/dL)">
            <input type="number" step="0.1" value={form.total_cholesterol_mg_dl}
                   onChange={(e) => setForm({ ...form, total_cholesterol_mg_dl: e.target.value })}
                   data-testid="lipid-input-tc"
                   placeholder="185" className="ros-input" required />
          </Field>
          <Field label="LDL (mg/dL)">
            <input type="number" step="0.1" value={form.ldl_mg_dl}
                   onChange={(e) => setForm({ ...form, ldl_mg_dl: e.target.value })}
                   data-testid="lipid-input-ldl"
                   placeholder="105" className="ros-input" required />
          </Field>
          <Field label="HDL (mg/dL)">
            <input type="number" step="0.1" value={form.hdl_mg_dl}
                   onChange={(e) => setForm({ ...form, hdl_mg_dl: e.target.value })}
                   data-testid="lipid-input-hdl"
                   placeholder="48" className="ros-input" required />
          </Field>
          <Field label="Triglycerides (mg/dL)">
            <input type="number" step="0.1" value={form.triglycerides_mg_dl}
                   onChange={(e) => setForm({ ...form, triglycerides_mg_dl: e.target.value })}
                   data-testid="lipid-input-trig"
                   placeholder="130" className="ros-input" required />
          </Field>
        </div>
        {error && (
          <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="lipid-error">
            <AlertCircle size={14} /> {error}
          </div>
        )}
        <div>
          <button type="submit" disabled={saving}
                  data-testid="lipid-submit"
                  className="flex items-center gap-2 px-5 py-2 rounded-full bg-[#F87171] text-black font-semibold hover:bg-[#EF4444] disabled:opacity-50 transition-colors">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? "Saving…" : "Log panel"}
          </button>
        </div>
      </form>

      {chartData.length > 0 && (
        <div className="ros-card" data-testid="lipid-chart">
          <div className="font-heading text-lg mb-3">Lipid trend</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#272E2B" />
                <XAxis dataKey="date" stroke="#A3ACA7" fontSize={11} />
                <YAxis stroke="#A3ACA7" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0F1312", border: "1px solid #272E2B", borderRadius: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={100} stroke="#FBBF24" strokeDasharray="4 4" />
                <ReferenceLine y={150} stroke="#FB923C" strokeDasharray="4 4" />
                <Line type="monotone" dataKey="LDL"     stroke="#F87171" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="HDL"     stroke="#4ADE80" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Trig"    stroke="#FBBF24" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Non-HDL" stroke="#60A5FA" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="ros-card" data-testid="lipid-history">
        <div className="font-heading text-lg mb-3">History ({data.panels.length})</div>
        {data.panels.length === 0 ? (
          <div className="text-sm text-[#A3ACA7]">No panels yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] text-[#A3ACA7] uppercase tracking-wider">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">TC</th>
                  <th className="py-2 pr-4">LDL</th>
                  <th className="py-2 pr-4">HDL</th>
                  <th className="py-2 pr-4">Trig</th>
                  <th className="py-2 pr-4">Non-HDL</th>
                  <th className="py-2">Flags</th>
                </tr>
              </thead>
              <tbody>
                {data.panels.map((p) => (
                  <tr key={p.id} className="border-t border-[#272E2B]" data-testid={`lipid-row-${p.id}`}>
                    <td className="py-2 pr-4 text-white">{fmtDate(p.date)}</td>
                    <td className="py-2 pr-4 font-mono text-white">{p.total_cholesterol_mg_dl}</td>
                    <td className="py-2 pr-4 font-mono" style={{ color: p.flags?.ldl_high ? "#F87171" : "#4ADE80" }}>{p.ldl_mg_dl}</td>
                    <td className="py-2 pr-4 font-mono" style={{ color: p.flags?.hdl_low ? "#F87171" : "#4ADE80" }}>{p.hdl_mg_dl}</td>
                    <td className="py-2 pr-4 font-mono" style={{ color: p.flags?.trig_high ? "#F87171" : "#4ADE80" }}>{p.triglycerides_mg_dl}</td>
                    <td className="py-2 pr-4 font-mono text-[#A3ACA7]">{p.non_hdl_mg_dl}</td>
                    <td className="py-2 text-xs">
                      {p.flags?.ldl_high  && <span className="text-[#F87171] mr-2">LDL↑</span>}
                      {p.flags?.hdl_low   && <span className="text-[#F87171] mr-2">HDL↓</span>}
                      {p.flags?.trig_high && <span className="text-[#F87171]">Trig↑</span>}
                      {!p.flags?.ldl_high && !p.flags?.hdl_low && !p.flags?.trig_high && (
                        <span className="text-[#4ADE80] inline-flex items-center gap-1"><CheckCircle2 size={11} /> At target</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
